import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useNotify } from 'react-admin';
import {
  Box, Card, CardContent, Typography, Stack, Button, TextField,
  Autocomplete, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Alert, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import SaveIcon      from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InventoryIcon from '@mui/icons-material/Inventory';
import AddIcon       from '@mui/icons-material/Add';
import CloseIcon     from '@mui/icons-material/Close';
import authFetch, { responseError } from '../utils/authFetch';
import { card, btn, inputSx, NAVY, PURCHASE_STATUS, typo } from '../styles/ds';

const CURRENCY_CHOICES = [{ id: 'ILS', label: '₪ שקל' }, { id: 'USD', label: '$ דולר' }];
const fmtDate = iso => iso ? String(iso).slice(0, 10) : '';
const fmtCost = (v, currency) => currency === 'USD'
  ? `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  : `₪${Number(v || 0).toLocaleString('he-IL', { minimumFractionDigits: 2 })}`;
const fmtHistDate = d => d ? new Date(d).toLocaleDateString('he-IL') : '—';


// ── Quick Add Supplier ────────────────────────────────────────────────────────
const AddSupplierDialog = ({ open, onClose, onAdded }) => {
  const notify = useNotify();
  const [name,   setName]   = useState('');
  const [phone,  setPhone]  = useState('');
  const [email,  setEmail]  = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { notify('נא להזין שם ספק', { type: 'warning' }); return; }
    setSaving(true);
    try {
      const res = await authFetch('/suppliers', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), phone: phone || null, email: email || null }),
      });
      if (!res.ok) { notify(await responseError(res, 'שגיאה בהוספת ספק'), { type: 'error' }); return; }
      const supplier = await res.json();
      notify(`הספק "${supplier.name}" נוסף`, { type: 'success' });
      onAdded({ id: supplier.id, label: supplier.name });
      setName(''); setPhone(''); setEmail('');
      onClose();
    } catch { notify('שגיאה בהוספת ספק', { type: 'error' }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} dir="rtl" maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: NAVY }}>
        ספק חדש
        <IconButton size="small" onClick={onClose} aria-label="סגור"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} pt={1}>
          <TextField label="שם הספק *" value={name}  onChange={e => setName(e.target.value)}  fullWidth sx={inputSx} autoFocus />
          <TextField label="טלפון"     value={phone} onChange={e => setPhone(e.target.value)} fullWidth sx={inputSx} />
          <TextField label="אימייל"    value={email} onChange={e => setEmail(e.target.value)} fullWidth sx={inputSx} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: '#666' }}>ביטול</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          sx={{ backgroundColor: NAVY, borderRadius: 2, '&:hover': { backgroundColor: '#162d5a' } }}>
          הוסף ספק
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main PurchaseEdit ─────────────────────────────────────────────────────────
const PurchaseEdit = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const notify   = useNotify();

  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [receiving,    setReceiving]    = useState(false);
  const [purchase,     setPurchase]     = useState(null);

  const [variantId,    setVariantId]    = useState(null);
  const [supplierId,   setSupplierId]   = useState(null);
  const [quantity,     setQuantity]     = useState(1);
  const [unitCost,     setUnitCost]     = useState('');
  const [currency,     setCurrency]     = useState('ILS');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [notes,        setNotes]        = useState('');

  const [variants,        setVariants]        = useState([]);
  const [suppliers,       setSuppliers]       = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState(null);
  const [histLoading,     setHistLoading]     = useState(false);
  const [addSupplier,     setAddSupplier]     = useState(false);
  const autoFilledFor = useRef(null);

  const batchConsumed = purchase?.status === 'received'
    && purchase?.batch_original_qty != null
    && Number(purchase.batch_remaining_qty) < Number(purchase.batch_original_qty);

  const consumedQty = batchConsumed
    ? Number(purchase.batch_original_qty) - Number(purchase.batch_remaining_qty)
    : 0;

  const loadSuppliers = () =>
    authFetch('/suppliers?_start=0&_end=500&_sort=name&_order=ASC')
      .then(r => r.json())
      .then(d => setSuppliers(Array.isArray(d) ? d : []))
      .catch(() => {});

  // Load purchase + dropdown data in parallel
  useEffect(() => {
    setLoading(true);
    Promise.all([
      authFetch(`/purchases/${id}`).then(r => r.json()),
      authFetch('/variants?_start=0&_end=9999&_sort=name&_order=ASC').then(r => r.json()),
      authFetch('/suppliers?_start=0&_end=500&_sort=name&_order=ASC').then(r => r.json()),
    ]).then(([p, v, s]) => {
      if (p.error) { notify(p.error, { type: 'error' }); return; }
      setPurchase(p);
      setVariantId({ id: p.variant_id, label: p.variant_name || `וריאנט #${p.variant_id}` });
      setSupplierId(p.supplier_id ? { id: p.supplier_id, label: p.supplier_name || `ספק #${p.supplier_id}` } : null);
      setQuantity(p.quantity);
      setUnitCost(String(p.unit_cost));
      setCurrency(p.currency || 'ILS');
      setPurchaseDate(fmtDate(p.purchase_date));
      setNotes(p.notes || '');
      setVariants(Array.isArray(v) ? v : []);
      setSuppliers(Array.isArray(s) ? s : []);
      // This purchase's own price was just loaded — treat this variant/supplier
      // pair as already "auto-filled" so it isn't silently overwritten by the
      // latest-purchase auto-fill below. Only a deliberate change afterward
      // (a different variant or supplier) should trigger a fresh auto-fill.
      autoFilledFor.current = `${p.variant_id}-${p.supplier_id}`;
    }).catch(() => notify('שגיאה בטעינת הרכישה', { type: 'error' }))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  // Purchase history for this variant+supplier (excluding this purchase
  // itself) — drives both the price auto-fill and the small history table.
  useEffect(() => {
    if (!variantId?.id || !supplierId?.id) { setPurchaseHistory(null); return; }
    setHistLoading(true);
    authFetch(`/variants/${variantId.id}/purchase-history?supplier_id=${supplierId.id}&exclude_id=${id}`)
      .then(r => r.json())
      .then(d => setPurchaseHistory(d.rows || []))
      .catch(() => setPurchaseHistory([]))
      .finally(() => setHistLoading(false));
  }, [variantId?.id, supplierId?.id, id]);

  // Auto-fill cost from the most recent OTHER purchase of this pair — skipped
  // when stock fields are locked, and skipped on initial load (autoFilledFor
  // is seeded from this purchase's own loaded price above) so opening an
  // existing purchase never silently overwrites its historical cost; only a
  // deliberate variant/supplier change afterward triggers a fresh auto-fill.
  useEffect(() => {
    if (batchConsumed) return;
    if (!variantId?.id || !supplierId?.id || !purchaseHistory) return;
    const key = `${variantId.id}-${supplierId.id}`;
    if (autoFilledFor.current === key) return;
    const last = purchaseHistory[0];
    if (last) {
      setUnitCost(String(last.unit_cost));
      setCurrency(last.currency);
      autoFilledFor.current = key;
    }
  }, [supplierId?.id, variantId?.id, purchaseHistory, batchConsumed]);

  const variantOptions  = variants.map(v => ({ id: v.id, label: v.name || `וריאנט #${v.id}` }));
  const supplierOptions = suppliers.map(s => ({ id: s.id, label: s.name }));

  const handleSave = async () => {
    if (!variantId) { notify('נא לבחור מוצר', { type: 'warning' }); return; }
    if (!batchConsumed && (!unitCost || Number(unitCost) <= 0)) {
      notify('נא להזין עלות', { type: 'warning' }); return;
    }
    setSaving(true);
    try {
      // When batch is consumed only metadata fields may change — omit stock fields entirely
      // so the backend recognises this as a metadata-only update and skips the batch lock check.
      const body = batchConsumed
        ? { supplier_id: supplierId?.id || null, purchase_date: purchaseDate || null, notes: notes || null }
        : {
            variant_id:    variantId.id,
            supplier_id:   supplierId?.id || null,
            quantity:      Number(quantity) || 1,
            unit_cost:     Number(unitCost),
            currency,
            purchase_date: purchaseDate || null,
            notes:         notes || null,
          };

      const res  = await authFetch(`/purchases/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        notify(data.error || (res.status === 409
          ? 'לא ניתן לעדכן — חלק מהמלאי שנרכש כבר שומש בהזמנות או בייצור'
          : 'שגיאה בשמירה'), { type: 'error' });
        return;
      }
      notify('הרכישה עודכנה בהצלחה', { type: 'success' });
      if (data.usd_cost_converted) {
        notify('עלות הרכישה הומרה אוטומטית ל-₪ לפי שער הדולר הנוכחי', { type: 'info' });
      } else if (data.usd_cost_rate_missing) {
        notify('לא הוגדר שער דולר — עלות הרכישה ב-₪ לא עודכנה', { type: 'warning' });
      }
      navigate('/purchases');
    } catch {
      notify('שגיאה בשמירה', { type: 'error' });
    } finally { setSaving(false); }
  };

  const handleReceive = async () => {
    setReceiving(true);
    try {
      const res  = await authFetch(`/purchases/${id}/receive`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'שגיאה בקליטה');
      notify('הסחורה נקלטה למלאי', { type: 'success' });
      if (data.usd_cost_converted) {
        notify('עלות הרכישה הומרה אוטומטית ל-₪ לפי שער הדולר הנוכחי', { type: 'info' });
      } else if (data.usd_cost_rate_missing) {
        notify('לא הוגדר שער דולר — עלות הרכישה ב-₪ לא עודכנה', { type: 'warning' });
      }
      navigate('/purchases');
    } catch (err) {
      notify(err.message, { type: 'error' });
    } finally { setReceiving(false); }
  };

  if (loading) return <Box p={6} textAlign="center"><CircularProgress /></Box>;
  if (!purchase) return <Box p={4}><Typography color="error">רכישה לא נמצאה</Typography></Box>;

  const st          = PURCHASE_STATUS[purchase.status] || PURCHASE_STATUS.received;
  const stockLocked = batchConsumed;
  const lockedTip   = 'לא ניתן לשנות — חלק מהמלאי מרכישה זו כבר שומש';
  const lockedHint  = 'נעול — מלאי שומש';

  return (
    <Box className="crm-page" p={3}>
      {/* Header */}
      <Stack className="crm-wrap" direction="row" alignItems="center" spacing={2} mb={3} flexWrap="wrap" gap={1}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/purchases')}
          sx={{ color: NAVY, fontWeight: 600 }}>חזרה לרכישות</Button>
        <Typography {...typo.pageTitle} flex={1}>
          עריכת רכישה #{id}
        </Typography>
        <Chip label={st.label} sx={{ backgroundColor: st.bg, color: st.color, fontWeight: 700 }} />
      </Stack>

      {/* Conflict banner */}
      {stockLocked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>
            {Number(purchase.batch_remaining_qty) === 0
              ? `כל כמות הרכישה (${purchase.batch_original_qty} יח') כבר נוצלה`
              : `${consumedQty} מתוך ${purchase.batch_original_qty} יח' נוצלו בהזמנות או בייצור`}
          </strong>
          {' — לא ניתן לשנות מוצר, כמות, עלות או מטבע. ניתן לעדכן ספק, תאריך והערות.'}
        </Alert>
      )}

      {purchase.status === 'ordered' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          הרכישה טרם נקלטה למלאי — כל השדות ניתנים לעריכה.
        </Alert>
      )}

      {/* Form */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>

            {/* Variant */}
            <Tooltip title={stockLocked ? lockedTip : ''} placement="top">
              <span style={{ display: 'block' }}>
                <Autocomplete
                  options={variantOptions} value={variantId} disabled={stockLocked}
                  onChange={(_, v) => setVariantId(v)}
                  getOptionLabel={o => o?.label ?? ''} isOptionEqualToValue={(a, b) => a.id === b.id}
                  fullWidth
                  renderInput={p => (
                    <TextField {...p} label="מוצר *" sx={inputSx}
                      helperText={stockLocked ? lockedHint : ''}
                      FormHelperTextProps={{ sx: { color: '#e65100', fontSize: 11, mr: 0 } }} />
                  )}
                />
              </span>
            </Tooltip>

            {/* Supplier + quick-add */}
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Autocomplete
                sx={{ flex: 1 }}
                options={supplierOptions} value={supplierId}
                onChange={(_, v) => setSupplierId(v)}
                getOptionLabel={o => o?.label ?? ''} isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={p => <TextField {...p} label="ספק" sx={inputSx} />}
              />
              <Button variant="contained" size="small" onClick={() => setAddSupplier(true)}
                title="הוסף ספק חדש"
                sx={{ backgroundColor: NAVY, color: '#fff', borderRadius: 2, minWidth: 40, height: 40, mt: 0.2, px: 1.2,
                  '&:hover': { backgroundColor: '#162d5a' } }}>
                <AddIcon fontSize="small" />
              </Button>
            </Stack>

            {/* Quantity */}
            <Tooltip title={stockLocked ? lockedTip : ''} placement="top">
              <TextField label="כמות *" type="number" value={quantity} fullWidth
                onChange={e => setQuantity(e.target.value)}
                inputProps={{ min: 1 }} sx={inputSx} disabled={stockLocked}
                helperText={stockLocked ? lockedHint : ''}
                FormHelperTextProps={{ sx: { color: '#e65100', fontSize: 11, mr: 0 } }} />
            </Tooltip>

            {/* Unit cost */}
            <Tooltip title={stockLocked ? lockedTip : ''} placement="top">
              <Box>
                <TextField label="עלות ליחידה *" type="number" value={unitCost} fullWidth
                  onChange={e => { setUnitCost(e.target.value); autoFilledFor.current = null; }}
                  inputProps={{ min: 0, step: '0.01' }} sx={inputSx} disabled={stockLocked}
                  helperText={stockLocked ? lockedHint : ''}
                  FormHelperTextProps={{ sx: { color: '#e65100', fontSize: 11, mr: 0 } }} />
                {!stockLocked && purchaseHistory?.[0] && (
                  <Typography fontSize={11} color="text.secondary" mt={0.4}>
                    מחיר ברכישה האחרונה מספק זה: {fmtCost(purchaseHistory[0].unit_cost, purchaseHistory[0].currency)}
                  </Typography>
                )}
              </Box>
            </Tooltip>

            {/* Currency */}
            <Tooltip title={stockLocked ? lockedTip : ''} placement="top">
              <span style={{ display: 'block' }}>
                <FormControl fullWidth disabled={stockLocked}>
                  <InputLabel>מטבע</InputLabel>
                  <Select value={currency} onChange={e => setCurrency(e.target.value)} label="מטבע" sx={inputSx}>
                    {CURRENCY_CHOICES.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
                  </Select>
                  {stockLocked && (
                    <Typography fontSize={11} sx={{ color: '#e65100', mt: 0.5, pr: 1.5 }}>{lockedHint}</Typography>
                  )}
                </FormControl>
              </span>
            </Tooltip>

            {/* Purchase date */}
            <TextField label="תאריך רכישה" type="date" value={purchaseDate} fullWidth
              onChange={e => setPurchaseDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={inputSx} />
          </Box>

          <TextField label="הערות" multiline rows={3} value={notes}
            onChange={e => setNotes(e.target.value)} fullWidth sx={{ mt: 2, ...inputSx }} />

          {/* Purchase history for this variant + supplier (other purchases) */}
          {supplierId?.id && variantId?.id && (
            <Box mt={2.5}>
              <Typography fontSize={12} fontWeight={700} color="text.secondary" mb={0.5}>
                רכישות קודמות של מוצר זה מספק זה
              </Typography>
              {histLoading ? (
                <CircularProgress size={16} />
              ) : !purchaseHistory?.length ? (
                <Typography fontSize={12} color="text.secondary">אין רכישות קודמות</Typography>
              ) : (
                <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table size="small" sx={{ maxWidth: 480 }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { backgroundColor: 'rgba(13,29,62,0.07)', fontSize: 11, py: 0.5, fontWeight: 700 } }}>
                      <TableCell>תאריך</TableCell>
                      <TableCell align="center">כמות</TableCell>
                      <TableCell align="center">עלות ליחידה</TableCell>
                      <TableCell align="center">סטטוס</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseHistory.map(p => (
                      <TableRow key={p.id} sx={{ '& td': { fontSize: 12, py: 0.5 } }}>
                        <TableCell>{fmtHistDate(p.purchase_date)}</TableCell>
                        <TableCell align="center">{p.quantity}</TableCell>
                        <TableCell align="center">{fmtCost(p.unit_cost, p.currency)}</TableCell>
                        <TableCell align="center">{p.status === 'ordered' ? 'הוזמן' : 'התקבל'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Stack className="crm-header crm-wrap" direction="row" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap" gap={1}>
        <Box>
          {purchase.status === 'ordered' && (
            <Button variant="outlined" onClick={handleReceive} disabled={receiving}
              startIcon={receiving ? <CircularProgress size={14} /> : <InventoryIcon />}
              sx={{ borderColor: '#155724', color: '#155724', borderRadius: 2, fontWeight: 600 }}>
              קלוט למלאי
            </Button>
          )}
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={() => navigate('/purchases')} sx={btn.cancel}>ביטול</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={btn.primaryLg}>
            שמור שינויים
          </Button>
        </Stack>
      </Stack>

      <AddSupplierDialog
        open={addSupplier}
        onClose={() => setAddSupplier(false)}
        onAdded={s => {
          setSuppliers(prev => [...prev, { id: s.id, name: s.label }]);
          setSupplierId(s);
          loadSuppliers();
        }}
      />
    </Box>
  );
};

export default PurchaseEdit;
